import { getUploadVoiceUrl } from "site/utils/audio-path";

/** play() interrupted by pause() — normal when switching clips or closing UI. */
export function isBenignAudioPlayError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  return error instanceof Error && error.name === "AbortError";
}

export async function safeAudioPlay(audio: HTMLAudioElement): Promise<void> {
  try {
    await audio.play();
  } catch (error) {
    if (isBenignAudioPlayError(error)) {
      return;
    }
    throw error;
  }
}

function waitForCanPlay(audio: HTMLAudioElement): Promise<void> {
  if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const onReady = () => {
      cleanup();
      resolve();
    };
    const onFail = () => {
      cleanup();
      reject(new Error("audio load failed"));
    };
    const cleanup = () => {
      audio.removeEventListener("canplay", onReady);
      audio.removeEventListener("error", onFail);
    };
    audio.addEventListener("canplay", onReady);
    audio.addEventListener("error", onFail);
    audio.load();
  });
}

export async function playStorageAudioPreview(
  storagePath: string,
  cacheKey = "",
): Promise<HTMLAudioElement> {
  let src = getUploadVoiceUrl(storagePath);
  if (!src) {
    throw new Error("empty audio path");
  }
  const bust = cacheKey.trim();
  if (bust) {
    src = `${src}?v=${encodeURIComponent(bust)}`;
  }

  const audio = new Audio();
  audio.preload = "auto";
  audio.playsInline = true;
  audio.src = src;
  await waitForCanPlay(audio);
  await safeAudioPlay(audio);
  return audio;
}
